import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonType, Checkbox, Icon} from "@renta-apps/renta-react-components";
import {handleCompanyLinkClick, handleConstructionSiteLinkClick, handleDeviceDetailsLinkClick} from "@/helpers/NavigationHelper";
import {capitalizeWords} from "@/helpers/StringHelper";
import {EnabledAlertGridModel} from "@/pages/FleetMonitoring/AlertsGrid/AlertsGrid";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import {EnabledAlertDetails} from "@/models/server/EnabledAlertDetails";
import {DeviceGridRowValue} from "@/pages/FleetMonitoring/DeviceGrid/DeviceGridRow";
import Localizer from "@/localization/Localizer";
import styles from "./AlertsGrid.module.scss";
import AlertSubscriptionsManagement, {SubscriptionAlertConfig} from "@/components/AlertSubscriptionsManagement/AlertSubscriptionsManagement";
import {useAlertsSubscriptionsManagementState} from "@/components/AlertSubscriptionsManagement/useAlertsSubscriptionsManagementState";
import {SubscriptionAlertType} from "@/models/Enums";

interface IAlertsGridRowProps {
    item: EnabledAlertGridModel;
    index: number;
    expanded: boolean,
    selected: boolean,
    details: EnabledAlertDetails | null,
    onRowCheckboxClick: () => void,
    onAccordionButtonClick: () => Promise<void>,
    onSaveSubscriptionsAsync: (subscriptions: SubscriptionAlertConfig[]) => Promise<void>,
}

type SubscriptionIconConfig = { name: string, size: number, className: string };
const AlarmTypeToIconMap: Record<SubscriptionAlertType, SubscriptionIconConfig> = {
    [SubscriptionAlertType.Fuel]: {name: "droplet", size: 13, className: styles.fuelIcon},
    [SubscriptionAlertType.Battery]: {name: "batteryHalf", size: 14, className: styles.batteryIcon},
    [SubscriptionAlertType.Theft]: {name: "road", size: 14, className: styles.theftIcon},
    [SubscriptionAlertType.Idle]: {name: "clock", size: 12, className: styles.idleIcon},
    [SubscriptionAlertType.EnvironmentControlStructureInternalTemperature]: {name: "droplet", size: 12, className: styles.idleIcon},
};

const SubscriptionIcon: React.FC<SubscriptionAlertConfig> = ({type, supported, enabled}) => {
    const config = useMemo(() => AlarmTypeToIconMap[type], [type]);
    const color = useMemo(
        () => enabled
            ? RentaEasyConstants.Colors.enabledSubscriptionIconColor
            : supported
                ? RentaEasyConstants.Colors.supportedSubscriptionIconColor
                : RentaEasyConstants.Colors.disabledSubscriptionIconColor,
        [enabled, supported]
    );

    return (
        <Icon name={config.name} size={config.size} color={color} className={config.className}/>
    );
};

const AlertsGridRow: React.FC<IAlertsGridRowProps> = ({
    item,
    index,
    expanded,
    selected,
    details,
    onRowCheckboxClick,
    onAccordionButtonClick,
    onSaveSubscriptionsAsync,
}) => {
    const [
        alertsConfig,
        updateSubscription,
        updateAlertData,
        updateAdjustmentState,
        setSubscriptions,
    ] = useAlertsSubscriptionsManagementState(item.alertsConfig);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setSubscriptions(item.alertsConfig);
    }, [item.alertsConfig]);

    const formatName = useCallback((name: string | undefined): string => {
        return name ? capitalizeWords(name) : '-';
    }, []);

    const handleSaveButtonClick = async () => {
        setSaving(true);
        await onSaveSubscriptionsAsync(alertsConfig!);
        setSaving(false);
    };

    return (
        <div className={`${styles.dataRow} ${index % 2 === 0 ? "" : styles.odd}`}>
            <div className={`${styles.gridRow}`}>
                <div className={styles.name}>
                    <div className={styles.checkboxCell}>
                        <Checkbox checked={selected} onChange={onRowCheckboxClick}/>
                    </div>
                    <div className={`${styles.gridChild} ${styles.fontMedium}`}>
                        <span className={`${styles.link} ${styles.ellipsisText}`}
                              onClick={() => handleDeviceDetailsLinkClick(item.rentaId)}
                        >
                            {capitalizeWords(item.deviceName)}
                        </span>
                    </div>
                </div>
                <div className={styles.rentaId}>{item.rentaId}</div>
                <div className={styles.subscriptions}>
                    {item.alertsConfig.filter(alert => alert.supported).map((alert) => (
                        <SubscriptionIcon key={`${item.rentaId}_${alert.type}_icon`} type={alert.type} supported={alert.supported} enabled={!!alert.enabled}/>
                    ))}
                </div>
                <div className={styles.actions}>
                    <div className={styles.accordionButton} onClick={onAccordionButtonClick}>
                        <i className={`fas fa-angle-down ${expanded ? styles.rotated : ""}`}></i>
                    </div>
                </div>
            </div>
            <div className={`${styles.gridRow} ${styles.expandableGridRow} ${expanded ? styles.expandedRow : ""}`} data-cy="row-details">
                {details ? (
                    <>
                        <DeviceGridRowValue label={Localizer.fleetMonitoringPageGridProductGroup} extraClass={styles.productGroup}>
                            <span className={styles.ellipsisText}>{formatName(details?.productGroup)}</span>
                        </DeviceGridRowValue>
                        <DeviceGridRowValue label={Localizer.fleetMonitoringPageGridDepot} extraClass={styles.depot}>
                            <span className={styles.ellipsisText}>{formatName(details?.depot)}</span>
                        </DeviceGridRowValue>
                        <DeviceGridRowValue label={Localizer.genericConstructionSite} extraClass={styles.constructionSite}>
                            <span data-cy={"constructionSiteLink"}
                                  className={`${styles.ellipsisText} ${details?.constructionSiteId ? styles.link : ""}`}
                                  onClick={() => handleConstructionSiteLinkClick(details?.constructionSiteId)}
                            >
                                {formatName(details?.constructionSiteName)}
                            </span>
                        </DeviceGridRowValue>
                        <DeviceGridRowValue label={Localizer.genericCompany} extraClass={styles.company}>
                            <span data-cy={"customerLink"}
                                  className={`${styles.ellipsisText} ${details?.customerId ? styles.link : ""}`}
                                  onClick={() => handleCompanyLinkClick(details?.customerId)}
                            >
                                {formatName(details?.customerName)}
                            </span>
                        </DeviceGridRowValue>
                        <div className={styles.alertsManagement}>
                            <AlertSubscriptionsManagement disabled={saving}
                                                          singleDevice
                                                          itemKey={item.rentaId}
                                                          alerts={alertsConfig!}
                                                          onChange={updateSubscription}
                                                          onAlertDataChange={updateAlertData}
                                                          onAdjustmentChange={updateAdjustmentState}
                            />
                            {saving ? (
                                <div className={styles.pleaseWait}>
                                    <div>
                                        <Icon name="fa-solid fa-circle-notch fa-spin" size={24} color="#fe5000"/>
                                    </div>
                                    <div>
                                        {Localizer.fleetMonitoringPageSubscribeToAlertsModalPleaseWait}
                                    </div>
                                </div>
                            ) : (
                                <Button type={ButtonType.Primary}
                                        data-cy={`${item.rentaId}_save-button`}
                                        onClick={async () => await handleSaveButtonClick()}
                                        disabled={alertsConfig!.every((alert, index) =>
                                            alert.enabled === item.alertsConfig[index].enabled
                                            && JSON.stringify(alert.alarmData) === JSON.stringify(item.alertsConfig[index].alarmData)
                                        )}
                                >
                                    {Localizer.componentModalSaveChanges}
                                </Button>
                            )}
                        </div>
                    </>
                ) : (
                    <div>{Localizer.componentListLoading}</div>
                )}
            </div>
        </div>
    );
};

export default AlertsGridRow;